import { useMemo, useState } from "react";

// Paginated hex viewer for a Uint8Array. 16 bytes/row, 32 rows/page (512 bytes).
export default function HexViewer({ data, testid = "hex-viewer", highlight = [] }) {
  const PER_PAGE = 512;
  const [page, setPage] = useState(0);
  const [jump, setJump] = useState("");

  const total = data ? data.length : 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));
  const p = Math.min(page, pages - 1);
  const start = p * PER_PAGE;
  const end = Math.min(start + PER_PAGE, total);

  const rows = useMemo(() => {
    if (!data) return [];
    const r = [];
    for (let off = start; off < end; off += 16) {
      const slice = data.subarray(off, Math.min(off + 16, end));
      r.push({ off, slice });
    }
    return r;
  }, [data, start, end]);

  const hiSet = useMemo(() => new Set(highlight), [highlight]);

  if (!data) return null;

  const doJump = () => {
    const v = parseInt(jump.replace(/^0x/i, ""), 16);
    if (!isNaN(v)) setPage(Math.floor(Math.min(Math.max(v, 0), total - 1) / PER_PAGE));
  };

  return (
    <div data-testid={testid} className="rounded-lg border border-slate-800 bg-[#080b11] overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800 bg-[#0c1017] text-xs">
        <button data-testid={`${testid}-prev`} onClick={() => setPage(Math.max(0, p - 1))} disabled={p === 0}
          className="px-2 py-1 rounded bg-slate-800 text-slate-300 disabled:opacity-40 hover:bg-slate-700">Prev</button>
        <button data-testid={`${testid}-next`} onClick={() => setPage(Math.min(pages - 1, p + 1))} disabled={p >= pages - 1}
          className="px-2 py-1 rounded bg-slate-800 text-slate-300 disabled:opacity-40 hover:bg-slate-700">Next</button>
        <span className="font-mono-tt text-slate-500">Page {p + 1}/{pages} · 0x{start.toString(16).toUpperCase()}–0x{(end - 1).toString(16).toUpperCase()}</span>
        <div className="ml-auto flex items-center gap-1">
          <input data-testid={`${testid}-jump-input`} value={jump} onChange={(e) => setJump(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doJump()} placeholder="0x8000"
            className="w-24 h-7 rounded bg-[#121824] border border-slate-700 text-slate-100 px-2 font-mono-tt text-xs focus:outline-none focus:ring-1 focus:ring-cyan-400" />
          <button data-testid={`${testid}-jump-btn`} onClick={doJump} className="px-2 py-1 rounded bg-cyan-500 text-white hover:bg-cyan-400">Go</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="font-mono-tt text-xs leading-relaxed">
          <tbody>
            {rows.map(({ off, slice }) => (
              <tr key={off} className="hover:bg-slate-900/40">
                <td className="px-3 py-0.5 text-cyan-400/70 select-none whitespace-nowrap">{off.toString(16).toUpperCase().padStart(6, "0")}</td>
                <td className="px-3 py-0.5 whitespace-nowrap">
                  {Array.from({ length: 16 }).map((_, i) => {
                    const has = i < slice.length;
                    const abs = off + i;
                    const hi = hiSet.has(abs);
                    return (
                      <span key={i} className={`${i === 8 ? "ml-2 " : ""}${has ? (hi ? "text-red-400 font-bold bg-red-950/40 px-0.5 rounded" : "text-slate-300") : "text-slate-700"} mr-1`}>
                        {has ? slice[i].toString(16).toUpperCase().padStart(2, "0") : "  "}
                      </span>
                    );
                  })}
                </td>
                <td className="px-3 py-0.5 text-slate-500 whitespace-pre">
                  {Array.from(slice).map((b) => (b >= 0x20 && b < 0x7f ? String.fromCharCode(b) : ".")).join("")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
